import React from 'react';

import { useRouter } from 'next/router';

import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import { ButtonProps, Typography } from '@mui/material';
import classnames from 'classnames';

import { track } from '@klokhuis/core/Analytics/libs/analytics-lib';
import { AnalyticsEvent } from '@klokhuis/core/Analytics/types';
import { Location, PropertyTypeID } from '@klokhuis/core/ApiClient/Klokhuis';
import { useIntl } from '@klokhuis/core/Intl';
import { ListingSearchCityRouteParams, ROUTES } from '@klokhuis/core/Routing';
import { ListingsContext } from '@klokhuis/modules/Properties/context/ListingsContext';
import { useFilterStateDispatcher } from '@klokhuis/modules/Properties/hooks/useFilterStateDispatcher';
import { PropertyTypeIDTranslations } from '@klokhuis/modules/Properties/translations';

import { SearchView } from '../../types';
import { LocationAutocomplete } from '../LocationAutocomplete';

import { SubmitButton } from './components/SubmitButton';
import styles from './ListingSearchBar.module.css';

export interface ListingSearchBarProps {
  className?: string;
  submitButtonProps?: ButtonProps;
  isLocationRequired?: boolean;
  onLocationInputChange?: () => void;
  variant?: 'default' | 'pill';
}

export const ListingSearchBar = ({
  className,
  submitButtonProps,
  isLocationRequired,
  onLocationInputChange,
  variant = 'default',
}: ListingSearchBarProps) => {
  const { T, locale } = useIntl();
  const router = useRouter();
  const { listingsState } = ListingsContext.useContext();
  const { filterStateDispatcher } = useFilterStateDispatcher();

  // Derive location directly from context — stays in sync after client-side navigation
  const location = listingsState.filterResult.filters.location || undefined;

  const { listingTypeIds } = listingsState.filterResult.filters;

  const onLocationChange = React.useCallback(
    (selectedLocation: Location | null) => {
      filterStateDispatcher({
        ...listingsState.filterResult.filters,
        location: selectedLocation || undefined,
        pageNo: 1,
      });

      if (onLocationInputChange) {
        onLocationInputChange();
      }
    },
    [
      filterStateDispatcher,
      listingsState.filterResult.filters,
      onLocationInputChange,
    ],
  );

  const onSubmit = React.useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      track(AnalyticsEvent.ClickedSearchButton);

      function generateListingSearchPageUrl() {
        let listingTypeLabel: string;

        const listingTypeParams = new URLSearchParams();
        if (listingTypeIds?.length === 1) {
          listingTypeLabel = T(
            PropertyTypeIDTranslations[listingTypeIds[0] as PropertyTypeID],
          );
        } else {
          listingTypeLabel = T('listings_page.url.listing_type.generic');
        }
        for (const listingTypeId of listingTypeIds ?? []) {
          listingTypeParams.append('listingTypes', listingTypeId.toString());
        }
        let cityName: string;
        if (location?.name == null) {
          cityName = T('LBL_INDIA');
        } else {
          cityName = location.cityName.replaceAll(' ', '-');
        }
        const routeParams: ListingSearchCityRouteParams = {
          typeAndCity: `${listingTypeLabel.toLowerCase()}-${cityName.toLowerCase()}`,
        };

        if (
          listingTypeParams.getAll('listingTypes') &&
          listingTypeParams.getAll('listingTypes').length > 0
        ) {
          routeParams.listingTypes = listingTypeParams
            .getAll('listingTypes')
            ?.toString();
        }

        return ROUTES.ListingSearchCity.url(routeParams);
      }

      router.push(generateListingSearchPageUrl());
    },
    [router, listingTypeIds, location, locale, T],
  );

  return (
    <form
      autoComplete="off"
      className={classnames(styles.root, className)}
      role="search"
      onSubmit={onSubmit}
    >
      {variant === 'pill' ? (
        <div className={styles.pill}>
          <LocationOnOutlinedIcon className={styles.locationIcon} />
          <div className={styles.inputSection}>
            <LocationAutocomplete
              value={location as Location | null}
              borderless
              onChange={onLocationChange}
            />
          </div>
          <SubmitButton {...submitButtonProps} />
        </div>
      ) : (
        <div className={styles.locationAndRadiusContainer}>
          <LocationAutocomplete
            value={location as Location | null}
            borderless
            onChange={onLocationChange}
          />
          <SubmitButton {...submitButtonProps} />
        </div>
      )}

      {isLocationRequired && !location && (
        <div className={styles.errorMessage}>
          <Typography variant="caption" color="error">
            {T('listing_module.listing_search_bar.location_is_required')}
          </Typography>
        </div>
      )}
    </form>
  );
};
